// Published ski labels for readers (consumer inventory Section 7): one vocabulary per sport,
// shared with the v3 engine through skiScoreLabel (lib/conditions/ski/aggregate.ts).
//
// - Piste: Hero Snow, Firing, Great, Good, Challenging, Miserable (9 / 7.5 / 6.5 / 5 / 2).
// - Off-piste and touring: Hero, Firing, Great, Good, Fair, Poor, Flat (9 / 7.5 / 6.5 / 5 / 3.5 / 2).
// - A Closed status reads "Closed"; no lift access reads "No lift access" on piste and
//   off-piste (touring needs no lifts); a missing score reads "Score unavailable".
//
// The platform badge vocabulary (scoring.ts) stays in use for colour only. This module is
// pure and client safe: readers on the server and client components can both import it.
import { skiScoreLabel, SKI_LABEL_CLOSED, SKI_LABEL_NO_LIFT_ACCESS, SKI_LABEL_UNAVAILABLE } from "./ski/aggregate";
import type { SkiSport, SkiStatus } from "./ski/types";

export { SKI_LABEL_CLOSED, SKI_LABEL_NO_LIFT_ACCESS, SKI_LABEL_UNAVAILABLE };

export const SKI_SPORT_KEYS: readonly SkiSport[] = ["piste", "off-piste", "touring"];

/** Stored status vocabulary (status in the score provenance depth block, sports[sport], consumer inventory 6.1). */
export type SkiPublishedStatus = "open" | "closed" | "unknown" | "out-of-season";
export type SkiPublishedStatusReason = "resort-closed" | "lifts-closed" | "run-reports" | "season" | "no-lift-access";

export function isSkiSport(sport: string): sport is SkiSport {
  return sport === "piste" || sport === "off-piste" || sport === "touring";
}

/**
 * Engine status for a stored or engine status value. The stored vocabulary keeps the engine's
 * no_lift_access as `closed` with reason `no-lift-access`, and out of season as its own status;
 * both withhold the score, so out of season maps to closed. Anything unrecognised is unknown.
 */
export function engineSkiStatus(status: string | null | undefined, reason?: string | null): SkiStatus {
  if (status === "no_lift_access" || reason === "no-lift-access") return "no_lift_access";
  if (status === "closed" || status === "out-of-season") return "closed";
  if (status === "open") return "open";
  return "unknown";
}

/** True when the published score is withheld (Closed, or no lift access off touring). */
export function skiStatusWithholds(sport: SkiSport, status: string | null | undefined, reason?: string | null): boolean {
  const engine = engineSkiStatus(status, reason);
  return engine === "closed" || (engine === "no_lift_access" && sport !== "touring");
}

/**
 * Published label for a 0 to 10 score (one decimal, as stored in condition_scores and forecast
 * sportScores). The score is rounded to one decimal first so a label always follows the number
 * the reader shows.
 */
export function skiLabel10(
  sport: SkiSport,
  score10: number | null | undefined,
  status?: string | null,
  reason?: string | null,
): string {
  const score100 = typeof score10 === "number" && Number.isFinite(score10) ? Math.round(score10 * 10) : null;
  return skiScoreLabel(sport, score100, engineSkiStatus(status, reason));
}

/** Published label for a 0 to 100 score (daily_observations columns, provenance ranges). */
export function skiLabel100(
  sport: SkiSport,
  score100: number | null | undefined,
  status?: string | null,
  reason?: string | null,
): string {
  const score = typeof score100 === "number" && Number.isFinite(score100) ? Math.round(score100) : null;
  return skiScoreLabel(sport, score, engineSkiStatus(status, reason));
}

/**
 * Label for a history cell or a season overview figure (0 to 10). Ski sports use their own
 * vocabulary; other sports return null so the caller keeps its generic history vocabulary.
 */
export function skiHistoryLabel10(sport: string, score10: number | null | undefined): string | null {
  if (!isSkiSport(sport)) return null;
  return skiLabel10(sport, score10);
}
